import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Autocomplete from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import { Field } from "react-final-form";

const ITEMS_PER_ROW_SUGGESTIONS = ["2", "3", "4", "6"];

interface ItemsPerRowFieldProps {
  name: string;
  label?: string;
  fullWidth?: boolean;
}

/**
 * Поле «Карточек/элементов в ряд» блоков конструктора страниц (Contents,
 * Posts, Products, Packages, Services, ItemsList).
 *
 * БАГ, который чинит этот компонент: раньше поле рендерилось через общий
 * AutocompleteField БЕЗ freeSolo. MUI Autocomplete без freeSolo принимает
 * только значения из фиксированного списка options, явно выбранные кликом
 * или Enter по подсвеченному пункту списка. Значение, которое пользователь
 * печатает вручную — включая случай, когда напечатанный текст совпадает с
 * одним из пунктов, но не был выбран из выпадающего списка явно — никогда
 * не долетало до form-стейта: Autocomplete в этом случае просто показывает
 * "No options"/не подсвечивает ничего, а по клику на «Сохранить» (blur)
 * значение остаётся прежним. Итог для пользователя — «ввёл значение, нажал
 * Сохранить, значение потерялось» (воспроизведено и подтверждено вживую).
 *
 * Здесь то же самое поле, но freeSolo: подсказки 2/3/4/6 остаются, но по
 * blur/выбору текущий введённый текст всегда явно коммитится в форм-стейт
 * как число (или null, если поле очищено).
 */
export const ItemsPerRowField: React.FC<ItemsPerRowFieldProps> = ({
  name,
  label,
  fullWidth = true,
}) => {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t("platformBlocks.fields.itemsPerRow");

  return (
    <Field
      name={name}
      allowNull
      parse={(value: unknown) => {
        if (value === "" || value === null || value === undefined) {
          return null;
        }
        const num = Number(value);
        return Number.isFinite(num) && num > 0 ? Math.round(num) : null;
      }}
    >
      {({ input }) => (
        <ItemsPerRowAutocomplete
          value={(input.value as number | null | undefined) ?? null}
          onChange={input.onChange}
          onBlur={input.onBlur}
          label={resolvedLabel}
          fullWidth={fullWidth}
        />
      )}
    </Field>
  );
};

interface ItemsPerRowAutocompleteProps {
  value: number | null;
  onChange: (value: string) => void;
  onBlur: () => void;
  label?: string;
  fullWidth?: boolean;
}

function ItemsPerRowAutocomplete({
  value,
  onChange,
  onBlur,
  label,
  fullWidth,
}: ItemsPerRowAutocompleteProps) {
  const [inputValue, setInputValue] = useState(
    value != null ? String(value) : ""
  );

  // Держим отображаемый текст в синхроне с закоммиченным значением формы
  // (например, при смене значения снаружи или при первом рендере).
  useEffect(() => {
    setInputValue(value != null ? String(value) : "");
  }, [value]);

  return (
    <Autocomplete
      freeSolo
      fullWidth={fullWidth}
      options={ITEMS_PER_ROW_SUGGESTIONS}
      value={value != null ? String(value) : null}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onChange={(_event, newValue) => {
        onChange(newValue ?? "");
      }}
      onBlur={() => {
        // Коммитим то, что реально введено, даже если пользователь не
        // выбирал пункт из списка и не нажимал Enter — именно это раньше
        // терялось при клике на «Сохранить».
        onChange(inputValue);
        onBlur();
      }}
      renderInput={(params) => (
        <MuiTextField {...params} label={label} fullWidth={fullWidth} />
      )}
    />
  );
}
